import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Zap, 
  Users, 
  Trophy, 
  ChevronRight
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { getBreadcrumbSchema, getGameSchema } from "@/utils/structuredData";
import { supabase } from "@/integrations/supabase/client";
import { MentorCard } from "@/components/MentorCard";

const MentorPokemonTCG = () => {
  const navigate = useNavigate();
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPokemonMentors = async () => {
      try {
        setLoading(true);
        const { data: gameData } = await supabase
          .from("games")
          .select("id")
          .ilike("name", "%pokemon%")
          .single();

        if (gameData) {
          const { data: offerings } = await supabase
            .from("mentorship_offerings")
            .select(`
              mentor_id,
              mentors!inner (
                id, name, bio, image_url, slug, average_rating, total_reviews,
                mentor_info (current_rank, peak_rank, years_experience, specialties)
              )
            `)
            .eq("game_id", gameData.id)
            .eq("is_active", true);

          if (offerings) {
            const uniqueMentors = (offerings as any[]).reduce((acc: any[], offering: any) => {
              const mentor = offering.mentors;
              if (!acc.find(m => m.id === mentor.id)) {
                acc.push({ ...mentor, mentor_info: mentor.mentor_info?.[0] || {} });
              }
              return acc;
            }, []);
            setMentors(uniqueMentors);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar mentores de Pokemon TCG:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPokemonMentors();
  }, []);

  useSEO({
    title: "Mentores de Pokemon TCG - Coaches Profissionais | Arena Pro Player",
    description: `Encontre os melhores mentores de Pokemon TCG! ${mentors.length}+ coaches profissionais para te ajudar a construir decks competitivos e dominar torneios.`,
    keywords: "mentor pokemon tcg, coach pokemon tcg, professor pokemon tcg, aulas pokemon tcg, treino pokemon tcg, decks pokemon, torneios pokemon",
    canonicalUrl: "https://arena-pro-player.com/mentor-pokemon-tcg",
    structuredData: [
      getBreadcrumbSchema([
        { name: "Início", url: "https://arena-pro-player.com/" },
        { name: "Mentores", url: "https://arena-pro-player.com/mentores" },
        { name: "Mentores de Pokemon TCG", url: "https://arena-pro-player.com/mentor-pokemon-tcg" }
      ]),
      getGameSchema({ name: "Pokemon TCG", cover_url: "/pokemon-tcg-cover.jpg" }, mentors.length)
    ]
  });

  return (
    <div className="min-h-screen bg-[#0f141c] text-white">
      <section className="relative py-20 px-4 bg-gradient-to-br from-[#0f141c] via-[#1a2332] to-[#0f141c]">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-4 bg-[#FFCB05] text-black">
              <Zap className="h-4 w-4 mr-2" />
              Pokemon TCG
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Mentores de <span className="text-[#FFCB05]">Pokemon TCG</span>
              <br />
              <span className="text-2xl md:text-3xl text-gray-300">Coaches Profissionais</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Encontre os melhores mentores de Pokemon TCG! Coaches experientes para te ajudar 
              a construir decks competitivos e dominar torneios.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-[#FFCB05] hover:bg-[#E6B800] text-black"
                onClick={() => navigate("/como-jogar-pokemon-tcg")}
              >
                <Zap className="h-5 w-5 mr-2" />
                Como Jogar Pokemon TCG
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-[#FFCB05] text-[#FFCB05] hover:bg-[#FFCB05] hover:text-black"
                onClick={() => navigate("/mentorias?game=pokemon-tcg")}
              >
                <Trophy className="h-5 w-5 mr-2" />
                Ver Todas as Mentorias
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Nossos <span className="text-[#FFCB05]">Mentores</span> de Pokemon TCG
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto">
              Coaches profissionais com experiência comprovada em Pokemon TCG.
            </p>
          </motion.div>

          {mentors.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {mentors.map((mentor, index) => (
                <motion.div
                  key={mentor.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <MentorCard {...(mentor as any)} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚡</div>
              <h3 className="text-2xl font-bold text-white mb-2">Mentores em Breve!</h3>
              <p className="text-gray-300 mb-6">
                Estamos selecionando os melhores coaches de Pokemon TCG para você.
              </p>
              <Button 
                onClick={() => navigate("/como-jogar-pokemon-tcg")}
                className="bg-[#FFCB05] hover:bg-[#E6B800] text-black"
              >
                Enquanto isso, aprenda como jogar
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-r from-[#FFCB05] to-[#E6B800]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
              Pronto para Dominar Pokemon TCG?
            </h2>
            <p className="text-xl text-black/80 mb-8">
              Escolha seu mentor ideal e comece a treinar hoje mesmo.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-black text-[#FFCB05] hover:bg-gray-800"
                onClick={() => navigate("/mentores")}
              >
                <Users className="h-5 w-5 mr-2" />
                Ver Todos os Mentores
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MentorPokemonTCG; 